import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTrash,
  faSearch,
  faTimes,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";

const SprintCentralStory = () => {

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { projId } = useParams();

  const authState = useSelector((state) => state.authReducer || {});
  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "User";

  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(projId || "");
  const [stories, setStories] = useState([]);

  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 8;

  const [showModal, setShowModal] = useState(false);
  const [editingStory, setEditingStory] = useState(null);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedProjectId]);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  const initialForm = {
    name: "",
    description: "",
    storyPoints: 3,
    priority: "Medium",
    status: "Backlog",
  };

  const [formData, setFormData] = useState(initialForm);

  // -----------------------
  // Sample Data
  // -----------------------

  const demoProjects = [
    { id: 1, name: "Website Redesign 2026" },
    { id: 2, name: "ERP Integration Phase 1" },
    { id: 3, name: "Customer Support Portal" },
  ];

  const demoStories = [
    {
      id: 1,
      projectId: 1,
      name: "User Login Page",
      description: "As a user I want to login securely",
      storyPoints: 5,
      priority: "High",
      status: "Backlog",
    },
    {
      id: 2,
      projectId: 1,
      name: "Navigation Menu",
      description: "User should easily navigate pages",
      storyPoints: 3,
      priority: "Medium",
      status: "In Progress",
    },
    {
      id: 3,
      projectId: 2,
      name: "Inventory Sync Screen",
      description: "Display ERP inventory sync status",
      storyPoints: 8,
      priority: "High",
      status: "Backlog",
    },
  ];

  const loadSample = () => {
    setProjects(demoProjects);
    setStories(demoStories);
  };

  // -----------------------
  // CRUD
  // -----------------------

  const handleAddNew = () => {

    if (!selectedProjectId) {
      alert("Please select a project first.");
      return;
    }

    setEditingStory(null);
    setFormData(initialForm);
    setShowModal(true);

  };

  const handleEdit = (story) => {

    setEditingStory(story);

    setFormData({
      name: story.name,
      description: story.description,
      storyPoints: story.storyPoints,
      priority: story.priority,
      status: story.status,
    });

    setShowModal(true);

  };

  const handleDelete = (id) => {

    if (!window.confirm("Delete this story?")) return;

    setStories((prev) => prev.filter((s) => s.id !== id));

  };

  const handleSubmit = (e) => {

    e.preventDefault();

    const newStory = {
      id: editingStory?.id || Date.now(),
      projectId: Number(selectedProjectId),
      name: formData.name,
      description: formData.description,
      storyPoints: formData.storyPoints,
      priority: formData.priority,
      status: formData.status,
    };

    if (editingStory) {

      setStories((prev) =>
        prev.map((s) => (s.id === editingStory.id ? newStory : s))
      );

    } else {

      setStories((prev) => [...prev, newStory]);

    }

    setShowModal(false);
    setFormData(initialForm);

  };

  // -----------------------
  // Filtering
  // -----------------------

  const filteredProjectStories = stories.filter(
    (s) => String(s.projectId) === String(selectedProjectId)
  );

  const filtered = filteredProjectStories.filter(
    (s) =>
      s.name.toLowerCase().includes(search.toLowerCase()) ||
      (s.description || "").toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.ceil(filtered.length / perPage);

  const paginated = filtered.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage
  );

  const statusColor = {
    Backlog: "bg-indigo-600",
    "In Progress": "bg-emerald-600",
    Completed: "bg-gray-600",
    Blocked: "bg-red-600",
  };

  const priorityColor = {
    High: "text-red-400",
    Medium: "text-yellow-400",
    Low: "text-green-400",
  };

  return (

    <div className="min-h-screen bg-gray-900 text-gray-100 p-2">

      {/* HEADER */}

      <header className="sticky top-0 z-50 bg-black border-b border-blue-500/40 px-4 py-3 flex items-center justify-between">

        <div className="flex flex-col">

          <div className="text-lg lg:text-xl font-bold text-cyan-300">
            📘 SprintCentral Stories
          </div>

          <div className="text-xs text-gray-400">
            Manage User Stories & Agile Development
          </div>

        </div>

        <div className="hidden md:flex items-center gap-4">

          {isLoggedIn ? (
            <>
              <span className="text-xs text-teal-300">{userName}</span>
              <button
                onClick={handleLogout}
                className="w-12 px-2 py-1 bg-red-700/80 hover:bg-red-600 rounded-md text-sm"
              >
                ⏻
              </button>
            </>
          ) : (
            <button
              onClick={() => navigate("/login")}
              className="w-12 px-2 py-1 border border-teal-600 rounded-md"
            >
              🔐
            </button>
          )}

        </div>

      </header>

      {/* MAIN */}

      <div className="mt-4 min-h-[75vh]">

        <div className="mt-4 flex flex-col lg:flex-row gap-4">

          <button
            onClick={loadSample}
            className="text-xs lg:text-sm w-40 lg:w-64 px-2 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg"
          >
            <FontAwesomeIcon icon={faPlus} /> Sample Stories
          </button>

          <button
            onClick={handleAddNew}
            className="text-xs lg:text-sm w-48 px-2 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg"
          >
            <FontAwesomeIcon icon={faPlus} /> New Story
          </button>

          {!projId && (
            <select
              value={selectedProjectId}
              onChange={(e) => setSelectedProjectId(e.target.value)}
              className="bg-gray-800 border border-gray-700 rounded-lg h-12 px-3 py-2"
            >
              <option value="">Select Project</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          )}

          <div className="relative w-full">

            <FontAwesomeIcon
              icon={faSearch}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
            />

            <input
              type="text"
              placeholder="Search stories..."
              value={search}
              style={{ width: "100%" }}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg"
            />

          </div>

        </div>

        {/* MODAL */}

        {showModal && (
          <div className="bg-black w-full rounded-lg p-4 border border-cyan-200 shadow-xl mt-2">

            <div className="flex justify-between items-center mb-4">

              <h2 className="text-lg text-cyan-200 font-semibold">
                {editingStory ? "Edit Story" : "New Story"}
              </h2>

              <button
                className="w-12 bg-blue-500"
                onClick={() => setShowModal(false)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>

            </div>

            <form onSubmit={handleSubmit} className="space-y-3">

              <input
                type="text"
                placeholder="Story Title"
                value={formData.name}
                required
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                className="w-full bg-gray-900 border border-gray-700 rounded p-2"
              />

              <textarea
                placeholder="Story Description"
                style={{ height: "100px", width: "100%" }}
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                className="w-full bg-gray-900 border border-gray-700 rounded p-2"
              />

              <div className="flex gap-3">

                <input
                  type="number"
                  min="1"
                  value={formData.storyPoints}
                  onChange={(e) =>
                    setFormData({ ...formData, storyPoints: e.target.value })
                  }
                  className="w-32 bg-gray-900 border border-gray-700 rounded p-2"
                />

                <select
                  value={formData.priority}
                  onChange={(e) =>
                    setFormData({ ...formData, priority: e.target.value })
                  }
                  className="w-40 h-12 bg-gray-900 border border-gray-700 rounded p-2"
                >
                  <option value="High">High</option>
                  <option value="Medium">Medium</option>
                  <option value="Low">Low</option>
                </select>

                <select
                  value={formData.status}
                  onChange={(e) =>
                    setFormData({ ...formData, status: e.target.value })
                  }
                  className="w-40 h-12 bg-gray-900 border border-gray-700 rounded p-2"
                >
                  <option value="Backlog">Backlog</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Completed">Completed</option>
                  <option value="Blocked">Blocked</option>
                </select>

              </div>

              <button
                type="submit"
                className="ml-4 w-40 bg-cyan-600 hover:bg-cyan-500 rounded p-2"
              >
                {editingStory ? "Update Story" : "Create Story"}
              </button>

            </form>

          </div>
        )}

        {/* STORY CARDS */}

        <div className="mt-4 grid gap-4 grid-cols-1 lg:grid-cols-4">

          {paginated.map((story) => (

            <div
              key={story.id}
              className="bg-gray-800 border border-gray-700 rounded-xl p-5"
            >

              <div className="flex justify-between items-start">

                <h3 className="font-semibold text-white">{story.name}</h3>

                <span
                  className={`px-2 py-1 rounded text-xs ${statusColor[story.status]}`}
                >
                  {story.status}
                </span>

              </div>

              <p className="text-sm text-gray-300 mt-2">
                {story.description || "No story description"}
              </p>

              <div className="text-xs text-gray-400 mt-2">
                Story Points: {story.storyPoints}
              </div>

              <div className={`text-xs mt-2 ${priorityColor[story.priority]}`}>
                Priority: {story.priority}
              </div>

              <div className="mt-4 flex gap-2">

                <button
                  onClick={() => handleEdit(story)}
                  className="p-2 bg-gray-700 hover:bg-gray-600 rounded"
                >
                  <FontAwesomeIcon icon={faEdit} />
                </button>

                <button
                  onClick={() => handleDelete(story.id)}
                  className="p-2 bg-gray-700 hover:bg-gray-600 rounded"
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>

              </div>

            </div>

          ))}

        </div>

      </div>

      {/* FOOTER */}

      <footer className="bg-black border-t border-blue-400 text-white py-4">
        <div className="mx-auto px-3 flex flex-col lg:flex-row items-center justify-between gap-3">
          <div className="text-sm text-yellow-200">
            © 2026 Think5Whys Global DeepTech Solutions.
          </div>
          <div className="flex flex-wrap gap-4 text-sm">
            <Link to="/about-us" className="hover:text-teal-300">About</Link>
            <Link to="/privacy-policy" className="hover:text-teal-300">Privacy Policy</Link>
            <Link to="/terms-of-use" className="hover:text-teal-300">Terms of Use</Link>
            <Link to="/contact-us" className="hover:text-teal-300">Contact</Link>
          </div>
        </div>
      </footer>

    </div>

  );

};

export default SprintCentralStory;